import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axiosInstance from '@/api/axios.js';
import * as d3 from 'd3';
import useResizeObserver from 'use-resize-observer';

const TOOLTIP_ID = 'contamination-chart-tooltip';

// Belgian/Seenons België bin identifiers — PMD=blue, Paper=yellow for these
const BELGIAN_BIN_IDS = [
    'R2', 'R3', 'R4', 'R6', 'R7', 'R8', 'R9', 'R10',
    'R12', 'R14', 'R15', 'R16', 'R17', 'R19', 'R20', 'R21', 'R23', 'R24', 'R25','R28'
];

// Singleton tooltip helper — creates once, reuses forever
const getOrCreateTooltip = (isDarkMode) => {
    let tooltip = d3.select(`#${TOOLTIP_ID}`);
    if (!tooltip.empty()) {
        tooltip
            .style('background-color', isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)')
            .style('color', isDarkMode ? '#ffffff' : '#000000')
            .style('box-shadow', isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)')
            .style('border', isDarkMode ? '1px solid #444' : '1px solid #DDD');
        return tooltip;
    }

    return d3.select('body')
        .append('div')
        .attr('id', TOOLTIP_ID)
        .style('position', 'fixed')
        .style('pointer-events', 'none')
        .style('visibility', 'hidden')
        .style('background-color', isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)')
        .style('color', isDarkMode ? '#ffffff' : '#000000')
        .style('border-radius', '8px')
        .style('box-shadow', isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)')
        .style('border', isDarkMode ? '1px solid #444' : '1px solid #DDD')
        .style('padding', '12px')
        .style('font-size', '14px')
        .style('line-height', '1.5')
        .style('z-index', '9999')
        .style('max-width', '280px')
        .style('transition', 'opacity 0.1s ease');
};

const hideTooltip = () => {
    d3.select(`#${TOOLTIP_ID}`).style('visibility', 'hidden');
};

const showTooltip = (event, html, isDarkMode) => {
    const tooltip = getOrCreateTooltip(isDarkMode);

    const x = event.clientX + 14;
    const y = event.clientY - 14;

    const tooltipNode = tooltip.node();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = tooltipNode ? tooltipNode.offsetWidth || 280 : 280;
    const th = tooltipNode ? tooltipNode.offsetHeight || 150 : 150;

    tooltip
        .html(html)
        .style('visibility', 'visible')
        .style('left', (x + tw > vw ? x - tw - 20 : x) + 'px')
        .style('top', (y + th > vh ? y - th : y) + 'px');
};

const moveTooltip = (event) => {
    const tooltip = d3.select(`#${TOOLTIP_ID}`);
    if (tooltip.style('visibility') === 'hidden') return;

    const x = event.clientX + 14;
    const y = event.clientY - 14;
    const tooltipNode = tooltip.node();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = tooltipNode ? tooltipNode.offsetWidth || 280 : 280;
    const th = tooltipNode ? tooltipNode.offsetHeight || 150 : 150;

    tooltip
        .style('left', (x + tw > vw ? x - tw - 20 : x) + 'px')
        .style('top', (y + th > vh ? y - th : y) + 'px');
};

const ContaminationChart = ({
    bins,
    timeRange,
    className,
    title,
    customDateRange = [{ startDate: null, endDate: null, key: 'selection' }],
    isCustomDateRange = false
}) => {
    const chartRef = useRef(null);
    const svgRef = useRef(null);
    const cardRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [containerHeight] = useState(280);
    const [expandedLegendItem, setExpandedLegendItem] = useState(null);
    const [viewMode, setViewMode] = useState('percentage');

    const { width: containerWidth = 0 } = useResizeObserver({ ref: chartRef });

    // ── Belgian context detection ─────────────────────────────────────────────
    // Swaps PMD (yellow→blue) and Paper (blue→yellow) when the majority of
    // selected bins are Belgian/Seenons België bins.
    const isBelgianContext = (() => {
        if (!bins?.length) return false;
        const belgianCount = bins.filter(binId => BELGIAN_BIN_IDS.includes(String(binId))).length;
        return belgianCount > bins.length / 2;
    })();

    const wasteStreams = [
        { name: 'PMD',      color: isBelgianContext ? '#4084f4' : '#ffcc14', translationKey: 'PMD' },
        { name: 'Organic',  color: '#18a44c',                                 translationKey: 'Organic' },
        { name: 'Paper',    color: isBelgianContext ? '#ffcc14' : '#4084f4',  translationKey: 'Paper' },
        { name: 'Residual', color: '#f05c0c',                                 translationKey: 'Residual' }
    ];

    // Clean up singleton tooltip on unmount
    useEffect(() => {
        return () => {
            d3.select(`#${TOOLTIP_ID}`).remove();
        };
    }, []);

    // ── Weight helpers ────────────────────────────────────────────────────────
    const getTotalWeightForWasteStream = (wasteStreamName, data) => {
        if (!data?.per_bin_data) return 0;
        let total = 0;
        data.per_bin_data.forEach(bin => {
            const stream = bin.waste_streams.find(ws => ws.waste_stream === wasteStreamName);
            if (stream) total += stream.total_weight_kg || 0;
        });
        return total;
    };

    const getCleanWeightForWasteStream = (wasteStreamName, data) => {
        if (!data?.per_bin_data) return 0;
        let total = 0;
        data.per_bin_data.forEach(bin => {
            const stream = bin.waste_streams.find(ws => ws.waste_stream === wasteStreamName);
            if (stream) total += (stream.total_weight_kg || 0) - (stream.contaminated_weight_kg || 0);
        });
        return total;
    };

    const getContaminatedWeightForWasteStream = (wasteStreamName, data) => {
        if (!data?.per_bin_data) return 0;
        let total = 0;
        data.per_bin_data.forEach(bin => {
            const stream = bin.waste_streams.find(ws => ws.waste_stream === wasteStreamName);
            if (stream) total += stream.contaminated_weight_kg || 0;
        });
        return total;
    };

    const getContaminationRateByWeightForWasteStream = (wasteStreamName, data) => {
        const total = getTotalWeightForWasteStream(wasteStreamName, data);
        const contaminated = getContaminatedWeightForWasteStream(wasteStreamName, data);
        return total > 0 ? (contaminated / total) * 100 : 0;
    };

    const getCleanRateByWeightForWasteStream = (wasteStreamName, data) => {
        return 100 - getContaminationRateByWeightForWasteStream(wasteStreamName, data);
    };

    const formatContaminationItemName = (name) =>
        name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    // ── Dark mode observer ────────────────────────────────────────────────────
    useEffect(() => {
        const checkDarkMode = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
        checkDarkMode();
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    const getBackgroundColor = () => isDarkMode ? '#100c0c' : '#ffffff';

    // ── Reset on filter change ────────────────────────────────────────────────
    useEffect(() => {
        setIsLoading(true);
        setChartData(null);
        setError(null);
        if (chartRef.current) d3.select(chartRef.current).selectAll('*').remove();
    }, [bins, timeRange, customDateRange, isCustomDateRange]);

    // ── Data fetch ────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            try {
                const periodMap = { day: 'day', week: 'week', month: 'month', '3months': '3months', '6months': '6months', year: 'year' }
                const period = periodMap[timeRange] || 'week'

                if (!bins || bins.length === 0) {
                    setError('No bins selected')
                    setIsLoading(false)
                    return
                }

                const params = new URLSearchParams()

                if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                    const toLocalDate = (d) => {
                        const amsterdamDate = new Date(d.toLocaleString("en-US", { timeZone: "Europe/Amsterdam" }))
                        const pad = (n) => String(n).padStart(2, "0")
                        return `${amsterdamDate.getFullYear()}-${pad(amsterdamDate.getMonth() + 1)}-${pad(amsterdamDate.getDate())}`
                    }
                    const s = customDateRange[0].startDate instanceof Date ? customDateRange[0].startDate : new Date(customDateRange[0].startDate)
                    const e = customDateRange[0].endDate instanceof Date ? customDateRange[0].endDate : new Date(customDateRange[0].endDate)
                    params.append('start_date', toLocalDate(s))
                    params.append('end_date', toLocalDate(e))
                } else {
                    params.append('period', period)
                }

                bins.forEach(bin => params.append('bin', bin))

                const response = await axiosInstance.get(`/contamination-rates/?${params.toString()}`)

                if (response.data && response.data.contamination_rates) {
                    setChartData(response.data)
                } else {
                    setError('Invalid data format')
                }
            } catch (err) {
                console.error('Error fetching contamination data:', err)
                setError(err.response?.data?.error || 'Failed to load data')
                setChartData(null)
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [bins, timeRange, customDateRange, isCustomDateRange])
    useEffect(() => {
        if (isLoading && chartRef.current) {
            d3.select(chartRef.current).selectAll('*').remove();
            hideTooltip();
        }
    }, [isLoading]);

    // ── Re-render bar when viewMode / expandedItem changes ───────────────────
    useEffect(() => {
        if (!chartData?.contamination_rates || !expandedLegendItem) return;

        const wasteStream = wasteStreams.find(ws => ws.name === expandedLegendItem);
        if (!wasteStream) return;

        const item = chartData.contamination_rates.find(r =>
            r.waste_stream.toLowerCase() === wasteStream.name.toLowerCase()
        );
        if (!item) return;

        const container = document.getElementById(`bar-${wasteStream.name}`);
        if (!container) return;

        const wasteStreamData = buildWasteStreamData(wasteStream, item, chartData);
        d3.select(container).selectAll('*').remove();
        renderIndividualBar(wasteStreamData, container);
    }, [viewMode, expandedLegendItem, chartData, isDarkMode]);

    // ── Build data object ─────────────────────────────────────────────────────
    const buildWasteStreamData = (wasteStream, item, data) => ({
        wasteStream: wasteStream.name,
        color: wasteStream.color,
        translatedName: wasteStream.name,
        contaminationRate: item.contamination_rate,
        contaminatedItems: item.contaminated_items,
        totalItems: item.total_items,
        cleanRate: 100 - item.contamination_rate,
        hasData: true,
        contaminatedSubclasses: item.contaminated_subclasses || [],
        totalWeightKg: getTotalWeightForWasteStream(wasteStream.name, data),
        cleanWeightKg: getCleanWeightForWasteStream(wasteStream.name, data),
        contaminatedWeightKg: getContaminatedWeightForWasteStream(wasteStream.name, data),
        contaminationRateByWeight: getContaminationRateByWeightForWasteStream(wasteStream.name, data),
        cleanRateByWeight: getCleanRateByWeightForWasteStream(wasteStream.name, data),
    });

    // ── Circular chart ────────────────────────────────────────────────────────
    const renderCircularChart = () => {
        if (isLoading || error || !chartData?.contamination_rates?.length || !chartRef.current || !containerWidth) return;

        d3.select(chartRef.current).selectAll('*').remove();

        const contaminationRates = chartData.contamination_rates || [];
        const width = containerWidth;
        const height = containerHeight;
        const centerX = width / 2;
        const centerY = height / 2;
        const outerRadius = Math.min(width, height) * 0.4;
        const isSmallChart = width < 300 || height < 250;
        const labelOffset = isSmallChart ? Math.max(120, outerRadius + 30) : 100;

        const svg = d3.select(chartRef.current)
            .append('svg')
            .attr('width', width)
            .attr('height', height);

        svgRef.current = svg.node();

        const arcGenerator = d3.arc().startAngle(0).endAngle(3 * Math.PI / 2).cornerRadius(2);
        const contaminationArcGenerator = d3.arc().startAngle(0).cornerRadius(2);

        wasteStreams.forEach((wasteStream, index) => {
            const innerRadius = outerRadius - (index + 1) * 18 - 14;
            const outerRadiusRing = outerRadius - (index + 1) * 18;

            const item = contaminationRates.find(r =>
                r.waste_stream.toLowerCase() === wasteStream.name.toLowerCase()
            );

            const totalWeightKg = getTotalWeightForWasteStream(wasteStream.name, chartData);
            const contaminatedWeightKg = getContaminatedWeightForWasteStream(wasteStream.name, chartData);
            const cleanWeightKg = getCleanWeightForWasteStream(wasteStream.name, chartData);

            let contaminationValue, totalValue;
            if (viewMode === 'kg' && totalWeightKg > 0) {
                contaminationValue = contaminatedWeightKg;
                totalValue = totalWeightKg;
            } else {
                contaminationValue = item ? item.contamination_rate : 0;
                totalValue = 100;
            }

            // Background ring (clean portion)
            const backgroundRing = svg.append('path')
                .datum({ innerRadius, outerRadius: outerRadiusRing })
                .attr('d', arcGenerator)
                .attr('fill', wasteStream.color)
                .attr('transform', `translate(${centerX}, ${centerY})`)
                .style('cursor', 'pointer');

            // Contamination arc
            if (contaminationValue > 0) {
                const contaminationPercentage = viewMode === 'kg'
                    ? (contaminationValue / totalValue) * 100
                    : contaminationValue;
                const contaminationAngle = (contaminationPercentage / 100) * (3 * Math.PI / 2);

                const contaminationRing = svg.append('path')
                    .datum({ innerRadius, outerRadius: outerRadiusRing, endAngle: contaminationAngle })
                    .attr('d', contaminationArcGenerator)
                    .attr('fill', isDarkMode ? '#FFFFFF' : '#000000')
                    .attr('transform', `translate(${centerX}, ${centerY})`)
                    .style('cursor', 'pointer');

                contaminationRing
                    .on('mouseover', function (event) {
                        if (!item) return;
                        let contaminationDetails = item.contaminated_subclasses?.length
                            ? item.contaminated_subclasses.map(s => {
                                const w = viewMode === 'kg' && s.weight_kg ? ` (${s.weight_kg.toFixed(3)} kg)` : '';
                                return `<div>• ${formatContaminationItemName(s.name)}: ${s.count} items${w}</div>`;
                            }).join('')
                            : '<div>No contamination details available</div>';

                        showTooltip(event, `
    <div>
        <div style="font-weight:600;margin-bottom:6px;font-size:15px;">${wasteStream.name} - Contamination</div>
        <div>Rate: <b>${viewMode === 'kg' ? getContaminationRateByWeightForWasteStream(wasteStream.name, chartData).toFixed(1) : item.contamination_rate.toFixed(1)}%</b></div>
        <div>${viewMode === 'kg' ? 'Contaminated Weight' : 'Contaminated'}: <b>${viewMode === 'kg' ? contaminatedWeightKg.toFixed(2) + ' kg' : item.contaminated_items + ' items'}</b></div>
        <div style="margin-top:8px;font-weight:500;">Contaminants:</div>
        ${contaminationDetails}
    </div>
`, isDarkMode);
                    })
                    .on('mousemove', moveTooltip)
                    .on('mouseleave', hideTooltip);
            }

            // Background ring hover
            backgroundRing
                .on('mouseover', function (event) {
                    const cleanItems = item ? (item.total_items - item.contaminated_items) : 0;
                    const cleanPercentage = item ? (100 - item.contamination_rate) : 100;

                    showTooltip(event, `
                        <div>
                            <div style="font-weight:600;margin-bottom:6px;font-size:15px;">${wasteStream.name} - Clean</div>
                            <div>Clean Rate: <b>${cleanPercentage.toFixed(1)}%</b></div>
                            <div>Clean Items: ${cleanItems} items</div>
                            ${viewMode === 'kg' && cleanWeightKg > 0 ? `<div>Weight: <b>${cleanWeightKg.toFixed(2)} kg</b></div>` : ''}
                            <div>Total: ${item ? item.total_items : 0} items</div>
                        </div>
                    `, isDarkMode);
                })
                .on('mousemove', moveTooltip)
                .on('mouseleave', hideTooltip);

            // Labels
            let labelX = isSmallChart
                ? centerX - outerRadius + 70
                : centerX - outerRadius + labelOffset;
            let labelY = centerY - outerRadius + index * 18 + 25;

            if (labelX < 10) labelX = 10;
            if (labelY < 15) labelY = 15;
            if (labelY > height - 15) labelY = height - 15;

            svg.append('text')
                .attr('x', labelX)
                .attr('y', labelY)
                .attr('text-anchor', 'end')
                .attr('dominant-baseline', 'middle')
                .attr('font-size', isSmallChart ? '10px' : '11px')
                .attr('fill', isDarkMode ? '#E5E7EB' : '#374151')
                .attr('pointer-events', 'none')
                .text(wasteStream.name);
        });

        // Center text — average contamination rate across waste streams
        const rates = wasteStreams.map(ws => {
            const item = contaminationRates.find(r => r.waste_stream.toLowerCase() === ws.name.toLowerCase());
            return item ? item.contamination_rate : null;
        }).filter(r => r !== null);

        const avgRate = rates.length > 0 ? rates.reduce((sum, r) => sum + r, 0) / rates.length : 0;

        let centerDisplayValue = viewMode === 'kg' && chartData.total_contaminated_weight_kg
            ? `${chartData.total_contaminated_weight_kg.toFixed(2)} kg`
            : `${avgRate.toFixed(1)}%`;
        svg.append('text')
            .attr('x', centerX)
            .attr('y', centerY)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', isSmallChart ? '10px' : '12px')
            .attr('font-weight', 'bold')
            .attr('fill', isDarkMode ? '#FFFFFF' : '#000000')
            .attr('pointer-events', 'none')
            .text(centerDisplayValue);
    };

    // ── Individual bar ────────────────────────────────────────────────────────
    const renderIndividualBar = (wasteStreamData, containerElement) => {
        if (!wasteStreamData.hasData) return;

        d3.select(containerElement).selectAll('*').remove();

        const width = 280;
        const height = 40;
        const margin = { top: 10, right: 40, bottom: 10, left: 10 };
        const chartWidth = width - margin.left - margin.right;

        const svg = d3.select(containerElement)
            .append('svg')
            .attr('width', width)
            .attr('height', height);

        const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

        let cleanRate, contaminationRate;
        if (viewMode === 'kg' && wasteStreamData.totalWeightKg > 0) {
            cleanRate = wasteStreamData.cleanRateByWeight;
            contaminationRate = wasteStreamData.contaminationRateByWeight;
        } else {
            cleanRate = wasteStreamData.cleanRate;
            contaminationRate = wasteStreamData.contaminationRate;
        }

        const cleanWidth = (cleanRate / 100) * chartWidth;
        const contaminatedWidth = (contaminationRate / 100) * chartWidth;
        const barHeight = 20;

        const backgroundBar = g.append('rect')
            .attr('x', 0).attr('y', 10)
            .attr('width', chartWidth).attr('height', barHeight)
            .attr('fill', wasteStreamData.color).attr('opacity', 0.3).attr('rx', 2);

        const cleanBar = g.append('rect')
            .attr('x', 0).attr('y', 10)
            .attr('width', cleanWidth).attr('height', barHeight)
            .attr('fill', wasteStreamData.color).attr('rx', 2);

        let contaminatedBar;
        if (contaminationRate > 0) {
            contaminatedBar = g.append('rect')
                .attr('x', cleanWidth).attr('y', 10)
                .attr('width', contaminatedWidth).attr('height', barHeight)
                .attr('fill', isDarkMode ? '#dc2626' : '#ef4444').attr('rx', 2);
        }

        const makeCleanHtml = () => {
            const displayRate = viewMode === 'kg' ? cleanRate : wasteStreamData.cleanRate;
            return `
                <div>
                    <div style="font-weight:600;margin-bottom:6px;font-size:15px;">${wasteStreamData.translatedName} - Clean</div>
                    <div>Clean Rate: <b>${displayRate.toFixed(1)}%</b></div>
                    <div>Clean Items: ${wasteStreamData.totalItems - wasteStreamData.contaminatedItems} items</div>
                    ${viewMode === 'kg' && wasteStreamData.cleanWeightKg > 0 ? `<div>Weight: <b>${wasteStreamData.cleanWeightKg.toFixed(2)} kg</b></div>` : ''}
                </div>`;
        };

        const makeContaminatedHtml = () => {
            const displayRate = viewMode === 'kg' ? contaminationRate : wasteStreamData.contaminationRate;
            let details = wasteStreamData.contaminatedSubclasses?.length
                ? wasteStreamData.contaminatedSubclasses.map(s => {
                    const w = viewMode === 'kg' && s.weight_kg ? ` (${s.weight_kg.toFixed(3)} kg)` : '';
                    return `<div>• ${formatContaminationItemName(s.name)}: ${s.count} items${w}</div>`;
                }).join('')
                : '<div>No contamination details available</div>';

            return `
                <div>
                    <div style="font-weight:600;margin-bottom:6px;font-size:15px;">${wasteStreamData.translatedName} - Contamination</div>
                    <div>Rate: <b>${displayRate.toFixed(1)}%</b></div>
                    <div>Contaminated: ${wasteStreamData.contaminatedItems} items</div>
                    ${viewMode === 'kg' && wasteStreamData.contaminatedWeightKg > 0 ? `<div>Weight: <b>${wasteStreamData.contaminatedWeightKg.toFixed(2)} kg</b></div>` : ''}
                    <div style="margin-top:8px;font-weight:500;">Contaminants:</div>
                    ${details}
                </div>`;
        };

        const makeTotalHtml = () => `
            <div>
                <div style="font-weight:600;margin-bottom:6px;font-size:15px;">${wasteStreamData.translatedName} - Total</div>
                <div>Total Items: <b>${wasteStreamData.totalItems} items</b></div>
                ${viewMode === 'kg' && wasteStreamData.totalWeightKg > 0 ? `<div>Total Weight: <b>${wasteStreamData.totalWeightKg.toFixed(2)} kg</b></div>` : ''}
                <div>Clean: ${wasteStreamData.totalItems - wasteStreamData.contaminatedItems} items (${wasteStreamData.cleanRate.toFixed(1)}%)</div>
                <div>Contaminated: ${wasteStreamData.contaminatedItems} items (${wasteStreamData.contaminationRate.toFixed(1)}%)</div>
            </div>`;

        cleanBar
            .on('mouseover', (e) => showTooltip(e, makeCleanHtml(), isDarkMode))
            .on('mousemove', moveTooltip)
            .on('mouseleave', hideTooltip);

        if (contaminationRate > 0 && contaminatedBar) {
            contaminatedBar
                .on('mouseover', (e) => showTooltip(e, makeContaminatedHtml(), isDarkMode))
                .on('mousemove', moveTooltip)
                .on('mouseleave', hideTooltip);
        }

        backgroundBar
            .on('mouseover', (e) => showTooltip(e, makeTotalHtml(), isDarkMode))
            .on('mousemove', moveTooltip)
            .on('mouseleave', hideTooltip);
    };

    useEffect(() => {
        renderCircularChart();
    }, [chartData, isLoading, error, isDarkMode, containerHeight, containerWidth, viewMode]);

    const handleLegendClick = (wasteStreamName) => {
        setExpandedLegendItem(prev => prev === wasteStreamName ? null : wasteStreamName);
    };

    const handleViewModeChange = (mode) => setViewMode(mode);

    // ── CSV Export ────────────────────────────────────────────────────────────
    const handleExportCSV = async () => {
        if (!chartData?.contamination_rates) return

        // ── SECTION 1: Summary (aggregated per waste stream) ─────────────────
        const headers = [
            'Waste Stream', 'Total Items', 'Clean Items', 'Clean %',
            'Contaminated Items', 'Contamination %',
            'Total Weight (kg)', 'Clean Weight (kg)', 'Contaminated Weight (kg)', 'Contamination % by Weight',
            'Contaminant', 'Contaminant Count', 'Contaminant Weight (kg)'
        ]

        const rows = []
        chartData.contamination_rates.forEach(item => {
            const cleanItems = item.total_items - item.contaminated_items
            const cleanPercentage = 100 - item.contamination_rate
            const totalWeightKg = getTotalWeightForWasteStream(item.waste_stream, chartData)
            const cleanWeightKg = getCleanWeightForWasteStream(item.waste_stream, chartData)
            const contaminatedWeightKg = getContaminatedWeightForWasteStream(item.waste_stream, chartData)
            const contaminationRateByWeight = getContaminationRateByWeightForWasteStream(item.waste_stream, chartData)

            if (item.contaminated_subclasses?.length) {
                item.contaminated_subclasses.forEach((subclass, idx) => {
                    rows.push([
                        idx === 0 ? item.waste_stream : '',
                        idx === 0 ? item.total_items : '',
                        idx === 0 ? cleanItems : '',
                        idx === 0 ? cleanPercentage.toFixed(2) : '',
                        idx === 0 ? item.contaminated_items : '',
                        idx === 0 ? item.contamination_rate.toFixed(2) : '',
                        idx === 0 ? totalWeightKg.toFixed(3) : '',
                        idx === 0 ? cleanWeightKg.toFixed(3) : '',
                        idx === 0 ? contaminatedWeightKg.toFixed(3) : '',
                        idx === 0 ? contaminationRateByWeight.toFixed(2) : '',
                        formatContaminationItemName(subclass.name),
                        subclass.count,
                        (subclass.weight_kg || 0).toFixed(3)
                    ])
                })
            } else {
                rows.push([
                    item.waste_stream, item.total_items, cleanItems,
                    cleanPercentage.toFixed(2), item.contaminated_items,
                    item.contamination_rate.toFixed(2),
                    totalWeightKg.toFixed(3), cleanWeightKg.toFixed(3),
                    contaminatedWeightKg.toFixed(3), contaminationRateByWeight.toFixed(2),
                    '', '', ''
                ])
            }
        })

        let csv = `# Contamination Rates Export\n`
        csv += `# Export Date: ${new Date().toISOString().split('T')[0]}\n`
        csv += `# Period: ${timeRange}\n`
        csv += `# Bins: ${Array.isArray(bins) ? bins.join(', ') : bins}\n`
        csv += `# Overall Contamination Rate (by count): ${chartData.overall_contamination_rate.toFixed(2)}%\n`
        csv += `# Overall Contamination Rate (by weight): ${(chartData.overall_contamination_rate_by_weight || 0).toFixed(2)}%\n\n`
        csv += headers.join(',') + '\n'
        csv += rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n')

        const section1 = csv

        // ── SECTION 2: Individual contamination events with timestamps ────────
        let section2 = ''
        try {
            const params = { bins: Array.isArray(bins) ? bins.join(',') : bins }

            if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                const s = customDateRange[0].startDate
                const e = customDateRange[0].endDate
                params.startDate = (s instanceof Date ? s : new Date(s)).toISOString().slice(0, 10)
                params.endDate   = (e instanceof Date ? e : new Date(e)).toISOString().slice(0, 10)
            } else {
                params.timeRange = timeRange
            }

            const response = await axiosInstance.get('/stats_v2/raw-data', {
                params,
                paramsSerializer: { indexes: null },
            })

            if (response.data && Array.isArray(response.data)) {
                const contaminated = response.data.filter(
                    (item) => item.contamination === 'Y'
                )

                const detailHeaders = [
                    'Bin',
                    'Date & Time',
                    'Item',
                    'Thrown Into',
                    'Expected Stream',
                    'Contamination Y/N',
                    'Weight',
                ].join(',')

                const detailRows = contaminated.map((item) => {
                    // Convert UTC timestamp to Amsterdam time (CET/CEST)
                    let displayTime = '-'
                    if (item.time_collected && item.time_collected !== '-') {
                        try {
                            const dt = new Date(item.time_collected)
                            displayTime = dt.toLocaleString('nl-NL', {
                                timeZone: 'Europe/Amsterdam',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false,
                            })
                        } catch {
                            displayTime = item.time_collected
                        }
                    }
                    return [
                        item.bin || '-',
                        `"${displayTime}"`,
                        item.sub_class_waste_type || '-',
                        item.waste_type || '-',
                        item.waste_weight || 0,
                        item.expected_stream || '-',
                        item.contamination || '-',
                    ].join(",")
                })

                section2 = [
                    '\n\n',
                    '"--------------------------------------------------"',
                    '"--- SECTION 2: CONTAMINATION EVENTS (individual records with timestamps) ---"',
                    '"Each row is one contamination event. Use this to identify when and what was sorted incorrectly."',
                    '',
                    detailHeaders,
                    ...detailRows,
                ].join('\n')
            }
        } catch {
            section2 = '\n\n"Could not retrieve individual contamination records at this time."'
        }

        // ── Build and download ────────────────────────────────────────────────
        const finalCsv = section1 + section2
        const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `contamination_rates_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    // ── PNG Export ────────────────────────────────────────────────────────────
    const handleExportPNG = async () => {
        if (!cardRef.current) return;
        try {
            const htmlToImage = await import('html-to-image');
            const currentExpanded = expandedLegendItem;
            setExpandedLegendItem(null);
            await new Promise(r => setTimeout(r, 300));

            const dataUrl = await htmlToImage.toPng(cardRef.current, {
                backgroundColor: getBackgroundColor(),
                pixelRatio: 2,
                cacheBust: true,
            });

            const link = document.createElement('a');
            link.download = `contamination_chart_${timeRange}_${new Date().toISOString().split('T')[0]}.png`;
            link.href = dataUrl;
            link.click();

            if (currentExpanded) setTimeout(() => setExpandedLegendItem(currentExpanded), 300);
        } catch (err) {
            console.error('Error exporting PNG:', err);
            alert('Error exporting PNG. Please try again.');
        }
    };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <Card className={`flex flex-col h-full ${className || ''}`} style={{ backgroundColor: getBackgroundColor() }}>
                <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium text-center text-gray-900 dark:text-white">
                            {title || 'Contamination Rates'}
                        </h2>
                    </div>
                    {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                        <p className="text-sm text-muted-foreground text-center mb-2">
                            {(() => {
                                const s = customDateRange[0].startDate;
                                const e = customDateRange[0].endDate;
                                const sd = s instanceof Date ? s : new Date(s);
                                const ed = e instanceof Date ? e : new Date(e);
                                return `${sd.toLocaleDateString()} - ${ed.toLocaleDateString()}`;
                            })()}
                        </p>
                    )}
                    <div className="flex-1 flex items-center justify-center" style={{ height: `${containerHeight}px` }}>
                        <div className="animate-pulse w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    // ── Error state ───────────────────────────────────────────────────────────
    if (error) {
        return (
            <Card className={`flex flex-col h-full ${className || ''}`} style={{ backgroundColor: getBackgroundColor() }}>
                <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                    <h2 className="text-lg font-medium mb-4 text-center text-gray-900 dark:text-white">
                        {title || 'Contamination Rates'}
                    </h2>
                    {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                        <p className="text-sm text-muted-foreground text-center mb-2">
                            {(() => {
                                const s = customDateRange[0].startDate;
                                const e = customDateRange[0].endDate;
                                const sd = s instanceof Date ? s : new Date(s);
                                const ed = e instanceof Date ? e : new Date(e);
                                return `${sd.toLocaleDateString()} - ${ed.toLocaleDateString()}`;
                            })()}
                        </p>
                    )}
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4" style={{ height: `${containerHeight}px` }}>
                        <p className="text-3xl font-bold text-red-600 dark:text-red-400">Error</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 px-4">{error}</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // ── Main render ───────────────────────────────────────────────────────────
    return (
        <Card
            ref={cardRef}
            className={`flex flex-col h-full ${className || ''}`}
            style={{ backgroundColor: getBackgroundColor() }}
            data-card-export="true"
        >
            <CardContent className="flex-1 flex flex-col p-3 sm:p-4">
                <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                    <div className="flex-1 text-center">
                        <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                            {title || 'Contamination Rates'}
                        </h2>
                        {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                            <p className="text-xs text-muted-foreground mt-1">
                                {(() => {
                                    const s = customDateRange[0].startDate;
                                    const e = customDateRange[0].endDate;
                                    const sd = s instanceof Date ? s : new Date(s);
                                    const ed = e instanceof Date ? e : new Date(e);
                                    if (!isNaN(sd) && !isNaN(ed)) return `${sd.toLocaleDateString()} - ${ed.toLocaleDateString()}`;
                                    return '';
                                })()}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-2 ml-auto sm:ml-0">
                        {/* View mode toggle */}
                        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden shrink-0">
                            <button
                                onClick={() => handleViewModeChange('percentage')}
                                className={`px-2 py-1 text-xs font-medium transition-colors ${viewMode === 'percentage' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                            >%</button>
                            <button
                                onClick={() => handleViewModeChange('kg')}
                                className={`px-2 py-1 text-xs font-medium transition-colors ${viewMode === 'kg' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                                disabled={!chartData?.total_weight_kg}
                            >KG</button>
                        </div>

                        {/* Export dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary h-8 px-2 shrink-0">
                                    <Download className="h-4 w-4 mr-1" />
                                    <span className="text-xs">Export</span>
                                    <ChevronDown className="h-3 w-3 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Chart area */}
                <div className="flex-1 relative overflow-hidden" style={{ height: `${containerHeight}px` }}>
                    <div ref={chartRef} className="w-full h-full p-0" />
                </div>

                {/* Interactive Legend */}
                <div className="mt-4 space-y-2 text-xs">
                    <h3 className="font-semibold text-center mb-2 text-gray-900 dark:text-white">
                        Legend
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">(Click to expand details)</span>
                    </h3>

                    <div className="space-y-2">
                        {wasteStreams.map((wasteStream, index) => {
                            const item = chartData?.contamination_rates?.find(r =>
                                r.waste_stream.toLowerCase() === wasteStream.name.toLowerCase()
                            );

                            if (!item) {
                                return (
                                    <div key={`${wasteStream.name}-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800">
                                            <div className="flex items-center min-w-0 flex-1">
                                                <div className="w-4 h-4 mr-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: wasteStream.color }} />
                                                <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} text-sm font-medium truncate`}>
                                                    {wasteStream.name}
                                                </span>
                                            </div>
                                            <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs whitespace-nowrap`}>No Data</div>
                                        </div>
                                    </div>
                                );
                            }

                            const wasteStreamData = buildWasteStreamData(wasteStream, item, chartData);
                            const isExpanded = expandedLegendItem === wasteStream.name;

                            let displayValue, displaySubtext;
                            if (viewMode === 'kg' && wasteStreamData.totalWeightKg > 0) {
                                displayValue = `${wasteStreamData.contaminatedWeightKg.toFixed(2)} kg`;
                                displaySubtext = `${wasteStreamData.contaminatedItems}/${wasteStreamData.totalItems} items`;
                            } else {
                                displayValue = `${wasteStreamData.contaminationRate.toFixed(1)}%`;
                                displaySubtext = `${wasteStreamData.contaminatedItems}/${wasteStreamData.totalItems} items`;
                            }

                            return (
                                <div key={`${wasteStream.name}-${viewMode}-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                    <div
                                        className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        onClick={() => handleLegendClick(wasteStream.name)}
                                    >
                                        <div className="flex items-center min-w-0 flex-1">
                                            <div className="w-4 h-4 mr-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: wasteStream.color }} />
                                            <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} text-sm font-medium truncate`}>
                                                {wasteStreamData.translatedName}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="text-right">
                                                <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} font-medium text-sm whitespace-nowrap`}>
                                                    {displayValue}
                                                </div>
                                                <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs whitespace-nowrap`}>
                                                    {displaySubtext}
                                                </div>
                                            </div>
                                            <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                                            <div id={`bar-${wasteStream.name}`} className="flex justify-center" />
                                            <div className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                                <div className="space-y-1">
                                                    {viewMode === 'kg' && wasteStreamData.totalWeightKg > 0 ? (
                                                        <>
                                                            <div><strong>Total:</strong> {wasteStreamData.totalWeightKg.toFixed(2)} kg ({wasteStreamData.totalItems} items)</div>
                                                            <div><strong>Clean:</strong> {wasteStreamData.cleanWeightKg.toFixed(2)} kg ({wasteStreamData.cleanRateByWeight.toFixed(1)}%) - {wasteStreamData.totalItems - wasteStreamData.contaminatedItems} items</div>
                                                            <div><strong>Contaminated:</strong> {wasteStreamData.contaminatedWeightKg.toFixed(2)} kg ({wasteStreamData.contaminationRateByWeight.toFixed(1)}%) - {wasteStreamData.contaminatedItems} items</div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div>
                                                                <strong>Total:</strong> {wasteStreamData.totalItems} items
                                                                {wasteStreamData.totalWeightKg > 0 && ` (${wasteStreamData.totalWeightKg.toFixed(2)} kg)`}
                                                            </div>
                                                            <div>
                                                                <strong>Clean:</strong> {wasteStreamData.totalItems - wasteStreamData.contaminatedItems} items ({wasteStreamData.cleanRate.toFixed(1)}%)
                                                                {wasteStreamData.cleanWeightKg > 0 && ` (${wasteStreamData.cleanWeightKg.toFixed(2)} kg)`}
                                                            </div>
                                                            <div>
                                                                <strong>Contaminated:</strong> {wasteStreamData.contaminatedItems} items ({wasteStreamData.contaminationRate.toFixed(1)}%)
                                                                {wasteStreamData.contaminatedWeightKg > 0 && ` (${wasteStreamData.contaminatedWeightKg.toFixed(2)} kg)`}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center mb-1 flex-wrap">
                            <div className="w-3 h-3 mr-1 bg-black dark:bg-white flex-shrink-0" />
                            <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} text-xs text-center`}>
                                Black/White overlay shows contamination
                            </span>
                        </div>
                        <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs text-center px-2`}>
                            Colored bars show the clean waste portion
                        </div>
                    </div>

                    {chartData?.per_bin_data?.length > 1 && (
                        <div className={`mt-2 text-xs px-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <details className="mt-2">
                                <summary className="cursor-pointer text-xs font-medium">Per-Bin Breakdown</summary>
                                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                                    {chartData.per_bin_data.map((binData, index) => (
                                        <div key={index} className="text-xs p-1 break-words">
                                            <strong className="text-xs">{binData.bin}:</strong> {binData.contamination_rate.toFixed(1)}%
                                            ({binData.contaminated_items}/{binData.total_items} items)
                                            {viewMode === 'kg' && binData.total_weight_kg && (
                                                <> | {binData.contaminated_weight_kg?.toFixed(2)} kg / {binData.total_weight_kg?.toFixed(2)} kg</>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default ContaminationChart;